/**
 * Frontend Tool Handlers — 文件读取类
 *
 * 处理的工具：
 * - read_local_file / read_local_document / analyze_local_media → 统一文件提取
 * - read_file_content → 统一文件提取
 * - batch_read_files → 批量读取多个文件
 */

import {
  registerFrontendTool,
  extractFileContent,
  type FrontendToolContext,
} from '../frontend-dispatcher';
import { readFileAsBase64 } from '../../hooks/useFileSystem';

// ─── 统一文件提取 (read_local_file / read_local_document / analyze_local_media) ───
function registerUnifiedReadHandler(toolName: string) {
  registerFrontendTool(toolName, async (args, ctx) => {
    if (!args.path) return `[Error: Missing path argument for ${toolName}]`;

    ctx.setMessageStatus('searching', `⚡ 正在提取 ${args.path}...`);

    const question = args.question || (args.questions ? args.questions.join('; ') : undefined);
    const result = await extractFileContent(
      ctx.localDirHandle, args.path, ctx.apiBaseUrl, ctx.authToken, question,
    );
    return result.success ? result.content : `[Error: ${result.content}]`;
  });
}

registerUnifiedReadHandler('read_local_file');
registerUnifiedReadHandler('read_local_document');
registerUnifiedReadHandler('analyze_local_media');

// ─── read_file_content ──────────────────────
registerFrontendTool('read_file_content', async (args, ctx) => {
  if (!args.path) return `[Error: Missing path argument]`;

  ctx.setMessageStatus('searching', `⚡ 正在提取 ${args.path}...`);

  const result = await extractFileContent(
    ctx.localDirHandle, args.path, ctx.apiBaseUrl, ctx.authToken, args.question,
  );
  return result.success ? result.content : `[Error: ${result.content}]`;
});

// ─── batch_read_files ───────────────────────
registerFrontendTool('batch_read_files', async (args, ctx) => {
  if (!args.paths || !Array.isArray(args.paths)) {
    return `[Error: Missing paths argument for batch_read_files]`;
  }

  ctx.setMessageStatus('searching', `📚 正在批量读取 ${args.paths.length} 个文件...`);

  const fileResults: Record<string, string> = {};
  for (let i = 0; i < args.paths.length; i++) {
    const filePath = args.paths[i];
    const source = args.sources?.[i] || 'local';
    try {
      if (source === 'local') {
        const result = await extractFileContent(
          ctx.localDirHandle, filePath, ctx.apiBaseUrl, ctx.authToken,
        );
        fileResults[filePath] = result.success ? result.content : `[Error: ${result.content}]`;
      } else {
        fileResults[filePath] = `[Case file read not yet implemented in batch mode]`;
      }
    } catch (e: any) {
      fileResults[filePath] = `[Error: ${e?.message || e}]`;
    }
  }

  const parts = Object.entries(fileResults).map(([path, content]) =>
    `=== ${path} ===\n${content}`
  );
  return `[System Tool Response]: 批量读取完成 (${args.paths.length} 个文件)\n\n${parts.join('\n\n')}`;
});
